"use client";
import { DirectusImage } from "@/components/DirectusImage";
import { Button } from "@/components/ui/button";
import RouterLink from "@/components/ui/router-link";
import Title from "@/components/ui/title";
import { cn, getFileDownloadAdress } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import { ChevronsRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

export default function ComplianceConstruction(props: {
  data?: {
    title?: string;
    description?: string;
    files?: {
      name?: string;
      icon?: string;
      file?: string;
    }[];
  }
}) {
  const t = useScopedI18n("POWERQUARK简介");
  const [isMobile, setIsMobile] = useState<boolean>();
  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    const listener = () => {
      setIsMobile(
        window.matchMedia("(max-width: 768px)").matches
      );
    }
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    }
  }, []);
  return (
    <div className="w-full">
      {props.data?.title && (
        <Title title={props.data.title} underline={false} />
      )}
      <p className="mt-5 text-center text-sm md:mt-6 md:px-16 md:text-lg">{props.data?.description}</p>
      {!isMobile && (
        <div className="w-full mt-12 flex justify-center gap-x-7">
          {props.data?.files?.map((el, index) => (
            <div
              key={index}
              className="w-1/3 h-[350px] flex flex-col justify-center items-center rounded-2xl border border-zinc-200 hover-outline-shadow"
            >
              {el.icon && (
                <DirectusImage
                  id={el.icon}
                  alt={el.name || ""}
                  width={72}
                  height={72}
                />
              )}
              <p className="mt-6 max-w-[55%] text-lg font-bold text-center h-14">
                {el.name}
              </p>
              <RouterLink
                className="mt-5 flex items-center text-base text-primary"
                href={el.file ?
                  getFileDownloadAdress(
                    el.file || ""
                  ) :
                  undefined
                }
                target="_blank"
              >
                {t("前往查看")}
                <ChevronsRight size={16} />
              </RouterLink>
              <RouterLink
                className="mt-3.5"
                href={el.file ?
                  getFileDownloadAdress(
                    el.file || ""
                  ) :
                  undefined
                }
                target="_blank"
                download={el.name}
              >
                <Button
                  variant="outline"
                  className="min-w-[200px] rounded-lg"
                >
                  {t("点击下载PDF")}
                </Button>
                
              </RouterLink>
            </div>
          ))}
        </div>
      )}
      {isMobile && (
        <Carousel
          className="mt-5 w-full"
          opts={{ loop: true }}
          setApi={setMainApi}
        >
          <CarouselContent>
            {props.data?.files?.map((el, index) => (
              <CarouselItem
                key={index}
                className="basis-[68vw] p-2"
              >
                <div className="relative w-full h-[322px] select-none">
                  <div
                    className={cn(
                      "flex flex-col justify-center items-center  w-full h-[300px] px-7 rounded-lg",
                      {
                        "shadow-lg": current === index,
                        "border border-zinc-200": current !== index,
                      }
                    )}
                  >
                    {el.icon && (
                      <DirectusImage
                        id={el.icon}
                        alt={el.name || ""}
                        width={72}
                        height={72}
                      />
                    )}
                    <p className="mt-3 text-xl font-bold text-center h-14">
                      {el.name}
                    </p>
                    <RouterLink
                      className="mt-5 flex items-center text-base text-primary"
                      href={el.file ?
                        getFileDownloadAdress(
                          el.file || ""
                        ) :
                        undefined
                      }
                      target="_blank"
                    >
                      {t("前往查看")}
                      <ChevronsRight size={16} />
                    </RouterLink>
                    <RouterLink
                      className="mt-3.5 block w-full"
                      href={el.file ?
                        getFileDownloadAdress(
                          el.file || ""
                        ) :
                        undefined
                      }
                      target="_blank"
                      download={el.name}
                    >
                      <Button
                        variant="outline"
                        className="w-full rounded-lg"
                      >
                        {t("点击下载PDF")}
                      </Button>
                      
                    </RouterLink>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-4 flex w-full items-center justify-center space-x-2 md:hidden">
            {scrollSnaps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 w-1.5 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
                  {
                    "w-4 bg-[#2C2C2C]": current === index,
                  }
                )}></div>
            ))}
          </div>
        </Carousel>
      )}
    </div>
  )
}
